import { useNavigate, useParams } from 'react-router-dom'
import cn from 'classnames'
import { useShallow } from 'zustand/react/shallow'
import { getDownloadUrl, partialContentUpdate } from 'api/content'
import {
	ArrowShortIcon,
	DownloadIcon,
	ReturnIcon,
	UploadIcon,
} from 'assets/icons'
import StatusCircle from 'components/StatusCircle'
import Tooltip from 'components/Tooltip'
import useContent from 'hooks/api/useContent'
import { getDefaultUserPath, t } from 'helpers'
import { useAppStore, useCastingStore } from 'store'
import { ROLE_STATUS } from 'types/enums'
import s from './index.module.scss'

const ContentActions = (): JSX.Element => {
	const [assetId, contentId] = useCastingStore(
		useShallow((state) => [
			state.contentData.assetId,
			state.contentData.contentId,
		]),
	)
	const navigate = useNavigate()
	const { roleId } = useParams()

	const { content, refreshData } = useContent(roleId, 'bag')

	const activeStatus = (content?.ContentCastingStatus || '') as ROLE_STATUS

	const statusProps: {
		[key: string]: {
			color: string
			activeLabel: string
			status: 'success' | 'warning' | 'error'
		} | null
	} = {
		[ROLE_STATUS.CASTING]: {
			color: 'var(--warning)',
			activeLabel: 'APP_CASTING_INPROGRESS_STATUS',
			status: 'warning',
		},
		[ROLE_STATUS.OPENED]: {
			color: 'var(--success)',
			activeLabel: 'APP_CASTING_OPENED_STATUS',
			status: 'success',
		},
		[ROLE_STATUS.CLOSED]: {
			color: 'var(--error)',
			activeLabel: 'APP_CASTING_CLOSED_STATUS',
			status: 'error',
		},
	}

	const onStatusChange = async (status: ROLE_STATUS): Promise<void> => {
		try {
			if (!roleId) return

			if (status === ROLE_STATUS.CLOSED) {
				const { setOpenCloseCastingSidebar } = useCastingStore.getState()
				setOpenCloseCastingSidebar(true)
			}

			await partialContentUpdate(roleId, {
				properties: {
					'Content:Casting:Status': status,
				},
			})
		} catch (error) {
			console.log('error:', error)
		} finally {
			refreshData()
		}
	}

	const uploadAsset = (): void => {
		const { setOpenAddSceneSidebar } = useAppStore.getState()
		setOpenAddSceneSidebar(true)
	}

	const downloadAsset = async (
		contentId: string,
		assetId: string,
	): Promise<void> => {
		try {
			const assetUrl = await getDownloadUrl(contentId, assetId)
			const a = document.createElement('a')
			a.href = assetUrl
			a.download = 'true'
			a.click()
		} catch (error) {
			console.log('error:', error)
		}
	}

	const openMessageSidebar = (): void => {
		const { setOpenCastingFeedbackSidebar } = useCastingStore.getState()
		setOpenCastingFeedbackSidebar(true)
	}

	return (
		<div
			className={cn('castingToolWrapper', s.container)}
			style={{}}
			data-role="content-actions">
			<div style={{ height: 'fit-content' }}>
				<Tooltip title="APP_BACK">
					<div
						className={cn(s.button, 'castingToolWrapper')}
						onClick={() => {
							const { castingOverviewRedirectionLink } =
								useCastingStore.getState()
							navigate(castingOverviewRedirectionLink || getDefaultUserPath())
						}}>
						<div>
							<ArrowShortIcon style={{ transform: 'rotate(180deg)' }} />
						</div>
					</div>
				</Tooltip>
			</div>
			<div style={{ height: 'fit-content' }}>
				<Tooltip title="APP_CASTING_FEEDBACK_TOOLTIP">
					<div
						className={cn(s.button, 'castingToolWrapper')}
						onClick={openMessageSidebar}>
						<div>
							<ReturnIcon color="var(--mono200)" />
						</div>
					</div>
				</Tooltip>
			</div>
			<div className={cn(s.statusWrapper, 'castingToolWrapper')}>
				{!statusProps?.[activeStatus] && (
					<div className={s.wrapper}>
						<StatusCircle
							status={'mono300'}
							size={12}
							style={{ position: 'relative', top: '0', right: '0' }}
						/>
						<span
							style={{
								color: 'var(--mono300)',
							}}>
							{t('APP_CASTING_NONE_STATUS')}
						</span>
					</div>
				)}
				{[ROLE_STATUS.OPENED, ROLE_STATUS.CASTING, ROLE_STATUS.CLOSED]?.map(
					(status, index) => (
						<div
							key={status}
							className={s.wrapper}
							onClick={() => onStatusChange(status)}
							style={{
								order: status !== activeStatus ? 0 : index + 10,
							}}>
							<StatusCircle
								status={statusProps[status]?.status || 'error'}
								size={12}
								style={{ position: 'relative', top: '0', right: '0' }}
							/>
							<span
								style={{
									color: statusProps?.[status]?.color,
								}}>
								{t(`APP_CASTING_${status?.toUpperCase()}_STATUS`)}
							</span>
						</div>
					),
				)}
			</div>
			<div style={{ height: 'fit-content' }}>
				<Tooltip title="APP_DOWNLOAD_ASSET">
					<div
						className={cn(s.button, 'castingToolWrapper')}
						onClick={() => downloadAsset(contentId, assetId)}>
						<div>
							<DownloadIcon color="var(--mono200)" />
						</div>
					</div>
				</Tooltip>
			</div>
			<div style={{ height: 'fit-content' }}>
				<Tooltip title="APP_CALLBACK">
					<div
						className={cn(s.button, 'castingToolWrapper')}
						onClick={uploadAsset}>
						<div>
							<UploadIcon color="var(--mono200)" />
						</div>
					</div>
				</Tooltip>
			</div>
		</div>
	)
}

export default ContentActions
